import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Award, CalendarDays, HandCoins, HeartHandshake, Radar, Repeat, ShieldCheck, Sparkles, Target, Users,
} from "lucide-react";

import { Card, CardLink, Metric, Pill, RangeSelect, WorldReach } from "@/components/dash/DashKit";
import { Loading } from "@/components/common/Chrome";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRY_NAME, money, num } from "@/lib/format";
import { SPAN_CLASS, HEIGHT_CLASS, clampSpan, clampRows } from "@/lib/gridSpan";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const SLICE = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-5))", "hsl(var(--chart-3))"];

const DEFAULT_SPAN = {
  kpi_total_donations: 3, kpi_active_programs: 3, kpi_beneficiaries: 3, kpi_impact_score: 3,
  kpi_compliance_status: 3, kpi_aml_posture: 3, kpi_recurring_income: 3,
  funding_overview: 4, donations_by_type: 4, program_reach: 4, program_overview: 12,
  recent_donations: 4, compliance_alerts: 4, top_programs: 12,
  charity_hours: 3, charity_money: 3, charity_people: 3, charity_compliance: 3,
};

const pretty = (key) => key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const Bar = ({ value, max, testId }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" data-testid={testId}>
    <div className="h-full rounded-full bg-primary transition-[width] duration-500"
         style={{ width: `${max > 0 ? Math.min((value / max) * 100, 100) : 0}%` }} />
  </div>
);

const WholeCard = ({ title, rows, testId, footer, bars = false }) => {
  const max = bars ? Math.max(...rows.map(([, , raw]) => raw || 0), 1) : 0;
  return (
    <Card title={title} testId={testId}>
      <div className={bars ? "space-y-2.5" : "space-y-1.5"}>
        {rows.length === 0 && (
          <div className="py-3 text-[12px] text-muted-foreground">{i18n.t("Nothing recorded in this period.")}</div>
        )}
        {rows.map(([label, value, raw]) => (
          <div key={label} className={bars ? "space-y-1" : ""}>
            <div className="flex items-baseline justify-between gap-3 text-[12px]">
              <span className="truncate text-muted-foreground">{label}</span>
              <span className="shrink-0 font-mono text-foreground/85">{value}</span>
            </div>
            {bars && <Bar value={raw || 0} max={max} testId={`whole-bar-${label.toLowerCase().replace(/\s+/g, "-")}`} />}
          </div>
        ))}
      </div>
      {footer && <div className="mt-3 text-[11px] text-muted-foreground">{footer}</div>}
    </Card>
  );
};

const tooltip = {
  contentStyle: {
    background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
    borderRadius: 10, fontSize: 12, boxShadow: "0 8px 24px -12px rgba(4,12,32,0.45)",
  },
  labelStyle: { color: "hsl(var(--muted-foreground))" },
};

const greeting = () => {
  const h = new Date().getHours();
  return i18n.t(h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");
};

const rangeLabel = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const locale = i18n.language === "hi" ? "hi-IN" : "en-GB";
  const fmt = (d) => d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
  return `${fmt(first)} - ${fmt(now)}`;
};

export default function ExecutiveDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [whole, setWhole] = useState(null);
  const [layout, setLayout] = useState(null);
  const [range, setRange] = useState(null);

  const load = useCallback(async (window) => {
    const impactPeriod = { "1M": "30d", "3M": "90d", "6M": "365d" }[window] || "365d";
    const [{ data: d }, whole] = await Promise.all([
      api.get(`/charity/dashboard/overview?range=${window}`),
      api.get(`/impact/overview?period=${impactPeriod}`).catch(() => null),
    ]);
    setData(d);
    setWhole(whole?.data || null);
  }, []);

  useEffect(() => {
    api.get("/admin/overview-layout")
      .then((r) => { setLayout(r.data); setRange(r.data.default_range || "6M"); })
      .catch(() => { setLayout({ widgets: {}, order: [] }); setRange("6M"); });
  }, []);

  useEffect(() => { if (range) load(range).catch(() => setData(false)); }, [range, load]);

  if (!data || !layout) return <Loading label={i18n.t("Aggregating money, impact and regulatory posture")} />;

  const order = layout.order?.length ? layout.order : Object.keys(DEFAULT_SPAN);
  const show = (key) => layout.widgets?.[key] !== false;
  const k = data.kpis;
  const firstName = (user?.name || "there").split(" ")[0];
  const ranger = (id) => <RangeSelect value={range} onChange={setRange} testId={id} />;

  const TILES = {
    kpi_total_donations: (
      <Metric key="kpi_total_donations" testId="kpi-total-donations" icon={HandCoins} tone="emerald"
              label={t("Total Donations")} value={money(k.total_donations, "INR", true)}
              delta={k.total_donations_delta} deltaLabel={t("vs previous period")} />
    ),
    kpi_active_programs: (
      <Metric key="kpi_active_programs" testId="kpi-active-programs" icon={Target} tone="indigo"
              label={t("Active Programs")} value={num(k.active_programs)} delta={k.active_programs_delta}
              deltaLabel={t("vs last period")} />
    ),
    kpi_beneficiaries: (
      <Metric key="kpi_beneficiaries" testId="kpi-beneficiaries" icon={Users} tone="teal"
              label={t("Beneficiaries Reached")} value={num(k.beneficiaries_reached)}
              delta={k.beneficiaries_delta} deltaLabel={t("vs last period")} />
    ),
    kpi_impact_score: (
      <Metric key="kpi_impact_score" testId="kpi-impact-score" icon={Award} tone="violet"
              label={t("Impact Score")}
              value={<span>{k.impact_score}<span className="text-muted-foreground">/100</span></span>}
              delta={k.impact_delta} deltaLabel={t("points gained")} />
    ),
    kpi_compliance_status: (
      <Metric key="kpi_compliance_status" testId="kpi-compliance-status" icon={ShieldCheck} tone="amber"
              label={t("Compliance Status")} value={`${k.compliance_pct}%`}
              footer={t("{{a}}/{{b}} published", { a: k.published_rules, b: k.total_rules })} />
    ),
    kpi_aml_posture: (
      <Metric key="kpi_aml_posture" testId="kpi-aml-posture" icon={Radar} tone="amber"
              label={t("AML Posture")} value={t("{{n}} flagged", { n: k.aml_flagged ?? 0 })}
              footer={t("{{a}} donors scored · {{b}} EDD open", { a: k.aml_assessed ?? 0, b: k.aml_open_cases ?? 0 })} />
    ),
    kpi_recurring_income: (
      <Metric key="kpi_recurring_income" testId="kpi-recurring-income" icon={Repeat} tone="teal"
              label={t("Recurring Income")} value={money(k.recurring_annualised || 0, "INR", true)}
              footer={t("{{n}} active mandates / year", { n: k.recurring_active ?? 0 })} />
    ),
  };

  const BLOCKS = {
    funding_overview: (
      <Card testId="panel-funding-overview" title={t("Funding Overview")} action={ranger("funding-range")}>
        <div className="flex h-full flex-col">
        <div className="min-h-[124px] w-full flex-1">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <AreaChart data={data.funding_overview} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
              <defs>
                <linearGradient id="fundGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.38} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" axisLine={false} tickLine={false}
                     tick={{ fontSize: 10.5, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis axisLine={false} tickLine={false}
                     tick={{ fontSize: 10.5, fill: "hsl(var(--muted-foreground))" }}
                     tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
              <Tooltip {...tooltip} formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="this_period" stroke="hsl(var(--chart-1))" strokeWidth={2.2}
                    fill="url(#fundGrad)" dot={false} />
              <Area type="monotone" dataKey="last_period" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5}
                    strokeDasharray="5 5" fill="none" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-5 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span className="h-0.5 w-4 rounded bg-[hsl(var(--chart-1))]" /> {t("This Period")}
          </span>
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span className="h-0.5 w-4 rounded border-t-2 border-dashed border-muted-foreground" /> {t("Last Period")}
          </span>
        </div>
        </div>
      </Card>
    ),
    donations_by_type: (
      <Card testId="panel-donations-by-type" title={t("Donations by Type")} action={ranger("donations-range")}>
        <div className="flex h-full items-center gap-3">
          <div className="relative h-[150px] w-[150px] shrink-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <PieChart>
                <Pie data={data.donations_by_type} dataKey="value" nameKey="name"
                     innerRadius={46} outerRadius={68} paddingAngle={2} stroke="none">
                  {data.donations_by_type.map((d, i) => (
                    <Cell key={d.name} fill={SLICE[i % SLICE.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltip} formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="text-center">
                <div className="font-display text-[16px] font-bold text-foreground">
                  {money(data.donations_by_type.reduce((a, b) => a + b.value, 0), "INR", true)}
                </div>
                <div className="text-[10px] text-muted-foreground">{t("Total")}</div>
              </div>
            </div>
          </div>
          <div className="w-full space-y-2">
            {data.donations_by_type.map((d, i) => (
              <div key={d.name} className="flex items-start gap-2" data-testid={`donation-type-${d.name.toLowerCase()}`}>
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: SLICE[i % SLICE.length] }} />
                <div>
                  <div className="text-[12px] font-medium text-foreground">{t(d.name)}</div>
                  <div className="text-[11.5px] text-muted-foreground">
                    {money(d.value, "INR", true)} <span className="opacity-70">({d.share}%)</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    ),
    program_overview: (
      <Card testId="panel-program-overview" title={t("Program Overview")} padded={false}
            action={
              <div className="flex items-center gap-2 pr-1">
                {ranger("program-overview-range")}
                <Link to="/charity/programs"><CardLink testId="view-all-program-overview">{t("View all")}</CardLink></Link>
              </div>
            }>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse" data-testid="program-overview-table">
            <thead>
              <tr className="sticky top-0 z-10 border-y border-border bg-muted/95 backdrop-blur">
                {["Program", "Region", "Budget", "Utilisation", "Lead indicator", "Beneficiaries", "Status"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.program_overview.map((p) => (
                <tr key={p.code || p.name} className="row-hover" data-testid={`program-overview-row-${p.code}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-[12.5px] font-medium text-foreground">{p.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{p.code}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-[12px] text-muted-foreground">
                    {COUNTRY_NAME[p.country] || p.country}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-[12px] text-foreground">
                    {money(p.spent, "INR", true)}
                    <span className="text-muted-foreground"> / {money(p.budget, "INR", true)}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-9 text-[12px] font-semibold text-foreground">{p.utilisation}%</span>
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(var(--teal))]"
                             style={{ width: `${Math.min(100, p.utilisation)}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-[12px] text-foreground">{p.indicator || "—"}</div>
                    {p.indicator && (
                      <div className="text-[10.5px] text-muted-foreground">
                        {t("{{a}} of {{b}} ({{p}}%)", { a: num(p.indicator_result), b: num(p.indicator_target), p: p.indicator_pct })}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-[12px] text-foreground">{num(p.beneficiaries)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <Pill tone={p.utilisation >= 80 ? "Low" : p.utilisation >= 40 ? "Medium" : "High"}
                          testId={`program-status-${p.code}`}>
                      {t(p.status)}
                    </Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    ),
    program_reach: (
      <Card testId="panel-program-reach" title={t("Program Reach")} action={ranger("reach-range")}>
        <WorldReach reach={data.program_reach} testId="world-reach-map" />
      </Card>
    ),
    recent_donations: (
      <Card testId="panel-recent-donations" title={t("Recent Donations")}
            action={<Link to="/charity/donations"><CardLink testId="view-all-donations">{t("View all donations")}</CardLink></Link>}>
        <div className="space-y-0.5">
          {data.recent_donations.map((d, i) => (
            <div key={`${d.donor}-${i}`} className="flex items-center gap-2.5 rounded-lg px-1 py-1.5 transition-colors duration-150 hover:bg-muted/60"
                 data-testid={`recent-donation-${i}`}>
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/12 text-[10px] font-semibold text-primary">
                {d.donor.split(" ").slice(0, 2).map((p) => p[0]).join("")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-medium text-foreground">{d.donor}</div>
                <div className="text-[10.5px] text-muted-foreground">{d.ago}</div>
              </div>
              <div className="text-right text-[12px] font-semibold text-foreground">
                {money(d.amount, d.currency)}
              </div>
            </div>
          ))}
        </div>
      </Card>
    ),
    compliance_alerts: (
      <Card testId="panel-compliance-alerts" title={t("Compliance Alerts")}
            action={<Link to="/compliance/overview"><CardLink testId="view-all-alerts">{t("View all alerts")}</CardLink></Link>}>
        <div className="space-y-1.5">
          {data.compliance_alerts.length === 0 && (
            <div className="py-5 text-center text-[12px] text-muted-foreground">{t("Nothing needs attention.")}</div>
          )}
          {data.compliance_alerts.map((a, i) => (
            <div key={`${a.title}-${i}`} className="flex items-start gap-2.5 rounded-lg border border-border p-2.5"
                 data-testid={`compliance-alert-${i}`}>
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-amber-500/12 text-amber-600 dark:text-amber-400">
                <ShieldCheck className="h-3 w-3" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-semibold text-foreground">{a.title}</div>
                <div className="text-[11px] text-muted-foreground">{a.detail}</div>
              </div>
              <Pill tone={a.severity} testId={`alert-severity-${i}`}>{t(a.severity)}</Pill>
            </div>
          ))}
        </div>
      </Card>
    ),
    top_programs: (
      <Card testId="panel-top-programs" title={t("Top Programs by Impact Score")}
            action={<Link to="/charity/programs"><CardLink testId="view-all-programs">{t("View all")}</CardLink></Link>}>
        <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
          {data.top_programs.map((p, i) => (
            <div key={p.code || p.name} className="flex items-center gap-2.5" data-testid={`top-program-${i}`}>
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/80 to-[hsl(var(--teal))]/80 text-white">
                {i % 2 === 0 ? <HeartHandshake className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-medium text-foreground">{p.name}</div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(var(--teal))] transition-[width] duration-700"
                       style={{ width: `${p.score}%` }} />
                </div>
              </div>
              <span className="shrink-0 text-[11.5px] font-semibold text-foreground">
                {p.score}<span className="text-muted-foreground">/100</span>
              </span>
            </div>
          ))}
        </div>
      </Card>
    ),
    charity_hours: whole && (
      <WholeCard testId="panel-charity-hours" title={t("Where the hours came from")}
                 footer={t("{{n}} hours logged, {{m}} still to approve", { n: num(whole.hours.total), m: num(whole.hours.awaiting_approval) })}
                 bars
                 rows={Object.entries(whole.hours.by_kind || {})
                   .sort((a, b) => b[1] - a[1]).map(([key, v]) => [t(pretty(key)), `${num(v)}h`, v])} />
    ),
    charity_money: whole && (
      <WholeCard testId="panel-charity-money" title={t("Money in and out")}
                 footer={t("Aid distributed {{v}}", { v: money(whole.money.aid_distributed, "INR", true) })}
                 rows={[[t("Income"), money(whole.money.income, "INR", true)],
                        [t("Of which donations"), money(whole.money.donations, "INR", true)],
                        [t("Spend"), money(whole.money.spend, "INR", true)],
                        [t("Payroll cost"), money(whole.money.payroll_cost, "INR", true)],
                        [t("Net"), money(whole.money.net, "INR", true)]]} />
    ),
    charity_people: whole && (
      <WholeCard testId="panel-charity-people" title={t("People involved")}
                 rows={Object.entries(whole.people || {}).map(([k, v]) => [t(pretty(k)), num(v)])} />
    ),
    charity_compliance: whole && (
      <WholeCard testId="panel-charity-compliance" title={t("Compliance and evidence")}
                 rows={Object.entries(whole.compliance || {}).map(([k, v]) => [t(pretty(k)), num(v)])} />
    ),
  };

  const WIDGETS = { ...TILES, ...BLOCKS };
  const items = order.filter((key) => WIDGETS[key] && show(key));
  const spanOf = (key) => clampSpan(layout.spans?.[key] ?? DEFAULT_SPAN[key] ?? 6);
  const rowsOf = (key) => clampRows(layout.heights?.[key] ?? 1);

  return (
    <div className="space-y-3.5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-[22px] font-bold tracking-tight text-foreground">
            {greeting()}, {firstName}! <span className="text-[18px]">👋</span>
          </h1>
          <p className="text-[12.5px] text-muted-foreground">
            {t("Here's what's happening across your organisation today.")}
          </p>
        </div>
        <button data-testid="dashboard-date-range"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[12px] font-medium text-foreground transition-colors duration-150 hover:border-primary/40">
          <span>{rangeLabel()}</span>
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>

      {items.length > 0 && (
        <div className="stagger grid grid-cols-2 items-stretch gap-3 xl:grid-cols-12"
             data-testid="overview-blocks">
          {items.map((key) => (
            <div key={key}
                 data-testid={`overview-slot-${key}`}
                 className={`${key.startsWith("kpi_") ? "col-span-1" : "col-span-2"} ${SPAN_CLASS[spanOf(key)]} ${HEIGHT_CLASS[rowsOf(key)]}`}>
              {WIDGETS[key]}
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && (
        <Card testId="panel-empty-overview" title={t("Nothing selected")}>
          <p className="text-[12.5px] text-muted-foreground">
            {t("An administrator has hidden every widget. Turn them back on in Settings → Overview layout.")}
          </p>
        </Card>
      )}
    </div>
  );
}
